import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Download, Sparkles, Loader2, Lock } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SensitivityBadge } from '@/features/documents/components/SensitivityBadge';

interface DocumentDetail {
  id: string;
  title: string;
  file_type: string;
  file_size: number;
  sensitivity: 'normal' | 'confidential';
  processing_status: string;
  storage_path: string | null;
  created_at: string;
  updated_at: string;
}

interface DocumentChunk {
  id: string;
  chunk_index: number;
  content: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentDetailPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const [aiSummary, setAiSummary] = useState<string | null>(null);

  const { data: doc, isLoading, error, refetch } = useQuery({
    queryKey: ['document', documentId],
    queryFn: async () => {
      const { data, error: fetchError } = await supabase
        .from('document')
        .select('*')
        .eq('id', documentId!)
        .single();
      if (fetchError) throw fetchError;
      return data as unknown as DocumentDetail;
    },
    enabled: !!documentId,
  });

  const { data: chunks } = useQuery({
    queryKey: ['document-chunks', documentId],
    queryFn: async () => {
      const { data } = await supabase
        .from('document_chunk')
        .select('id, chunk_index, content')
        .eq('document_id', documentId!)
        .order('chunk_index');
      return (data ?? []) as DocumentChunk[];
    },
    enabled: !!documentId,
  });

  const generateSummary = useMutation({
    mutationFn: async () => {
      const { data, error: aiError } = await supabase.functions.invoke('document-ai', {
        body: { action: 'summarize', document_id: documentId },
      });
      if (aiError) throw aiError;
      return data.result as string;
    },
    onSuccess: (result) => setAiSummary(result),
  });

  const handleDownload = async () => {
    if (!doc?.storage_path) return;
    const { data } = await supabase.storage
      .from('documents')
      .createSignedUrl(doc.storage_path, 60);
    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !doc) {
    return <ErrorAlert message={error ? String(error) : 'Document not found'} onRetry={refetch} />;
  }

  return (
    <div>
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/documents')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Documents
        </Button>
      </div>

      <PageHeader
        title={doc.title}
        action={
          <div className="flex gap-2">
            {doc.storage_path && (
              <Button variant="outline" onClick={handleDownload}>
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
            )}
          </div>
        }
      />

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-4">
          {/* Document content preview */}
          <Card>
            <CardHeader><CardTitle className="text-base">Content</CardTitle></CardHeader>
            <CardContent>
              {doc.processing_status === 'processing' ? (
                <div className="flex items-center gap-2 text-sm text-primary">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Document is being processed...
                </div>
              ) : (chunks ?? []).length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {chunks!.map((chunk) => (
                    <p key={chunk.id} className="text-sm text-muted-foreground">
                      {chunk.content}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No text content extracted. Download the file to view it.
                </p>
              )}
            </CardContent>
          </Card>

          {/* AI Summary */}
          <Card>
            <CardContent className="pt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => generateSummary.mutate()}
                disabled={generateSummary.isPending}
              >
                {generateSummary.isPending
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Sparkles className="mr-2 h-4 w-4" />}
                AI Summary
              </Button>
              {aiSummary && (
                <p className="mt-3 text-sm whitespace-pre-wrap">{aiSummary}</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type</span>
                <span className="uppercase text-xs">{doc.file_type.split('/').pop()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Size</span>
                <span>{formatFileSize(doc.file_size)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sensitivity</span>
                <SensitivityBadge sensitivity={doc.sensitivity} />
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant={doc.processing_status === 'completed' ? 'success' : 'secondary'}>
                  {doc.processing_status}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Uploaded</span>
                <span>{new Date(doc.created_at).toLocaleDateString()}</span>
              </div>
            </CardContent>
          </Card>

          {doc.sensitivity === 'confidential' && (
            <Card className="border-destructive/20 bg-destructive/5">
              <CardContent className="flex items-center gap-2 pt-6 text-sm">
                <Lock className="h-4 w-4 text-destructive" />
                <span>AI processing uses local model only</span>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
